import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { BannerList_banners_edges_node } from "@saleor/banners/types/BannerList";
import { BannerListUrlSortField } from "@saleor/banners/urls";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

export interface BannersListProps
  extends ListProps,
    ListActions,
    SortPage<BannerListUrlSortField> {
  defaultCurrency: string;
  banners: BannerList_banners_edges_node[];
  totalCount: number;
}

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colEnd: {},
      colName: {},
      colStart: {},
      colValue: {
        width: 200
      }
    },
    colEnd: {
      textAlign: "center"
    },
    colFeatured: {
      overflow: "hidden",
      paddingLeft: 0,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: 180
    },
    colName: {
      paddingLeft: 0
    },
    colStart: {
      textAlign: "center"
    },
    colStatus: {
      textAlign: "left",
      width: "150px"
    },
    colValue: {
      textAlign: "right"
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "BannerList" }
);

const numberOfColumns = 4;

const BannerList: React.FC<BannersListProps> = props => {
  const {
    settings,
    disabled,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    pageInfo,
    banners,
    isChecked,
    selected,
    toggle,
    toggleAll,
    totalCount,
    toolbar
  } = props;

  const classes = useStyles(props);

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={banners}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader arrowPosition="right" className={classes.colName}>
          <FormattedMessage
            defaultMessage="Banner Name"
            description="banner name"
          />
        </TableCellHeader>
        <TableCellHeader arrowPosition="right" className={classes.colFeatured}>
          <FormattedMessage
            defaultMessage="Featured in"
            description="featured in"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="center" className={classes.colStart}>
          <FormattedMessage
            defaultMessage="Start Date"
            description="sale start date"
          />
        </TableCellHeader>
        <TableCellHeader textAlign="center" className={classes.colEnd}>
          <FormattedMessage
            defaultMessage="End Date"
            description="sale end date"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            totalCount={totalCount}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          banners,
          (banner, index) => {
            const isSelected = banner ? isChecked(banner.id) : false;

            return (
              <TableRow
                className={!!banner ? classes.tableRow : undefined}
                hover={!!banner}
                key={banner ? banner.id : "skeleton"}
                onClick={banner ? onRowClick(banner.id) : undefined}
                selected={isSelected}
              >
                <TableCell padding="checkbox" id={`checkboxRow${index}`}>
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(banner.id)}
                  />
                </TableCell>
                <TableCell
                  className={classes.colName}
                  id={`bannerNameRow${index}`}
                >
                  {maybe<React.ReactNode>(() => banner.name, <Skeleton />)}
                </TableCell>
                <TableCell
                  className={classes.colFeatured}
                  id={`featuredInRow${index}`}
                >
                  {maybe<React.ReactNode>(
                    () => banner.verticals + ",",
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colStart}
                  id={`startDate${index}`}
                >
                  {banner && banner.startDate ? (
                    <Moment format="MM/DD/YYYY">{banner?.startDate}</Moment>
                  ) : banner && banner.startDate === null ? (
                    "-"
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colEnd} id={`endDate${index}`}>
                  {banner && banner.endDate ? (
                    <Moment format="MM/DD/YYYY">{banner?.endDate}</Moment>
                  ) : banner && banner.endDate === null ? (
                    "-"
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No banners found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
BannerList.displayName = "BannerList";
export default BannerList;
